// Pure logic behind the Profile page's sovereignty membrane — the per-section public/private
// flags that decide what your identity + presence shows in Commune/Eden versus what stays only
// yours. No fetching, no rendering: everything here is a plain function over the ego entity's
// attributes.profile_visibility map, testable with plain objects.
//
// The flags live on the ego (the network node with self: true), so they are suite-shared: when
// Commune and Eden read a keeper's profile, they read the SAME map through the future public-read
// endpoint (GET /profile/{handle}) and honour these exact choices. Diwa writes them here today.

/** Whether a profile section is shown to others (public) or kept to the keeper (private). */
export type Visibility = 'public' | 'private'

/** The three sections the Profile composes — each carries its own visibility flag. */
export type ProfileSection = 'identity' | 'sigils' | 'progression'

/** The full per-section membrane, as stored on ego.attributes.profile_visibility. */
export type ProfileVisibility = Record<ProfileSection, Visibility>

/**
 * Sensible defaults — the membrane a keeper starts with before they touch anything:
 *  - identity   public  (your name, what you do, expertise — the shareable "who")
 *  - sigils     public  (earned marks are meant to be worn)
 *  - progression private (your streak/activity is yours until you choose to show it)
 *
 * Goals are a special case within identity: they are NEVER shown, regardless of the identity
 * flag (see GOALS_ALWAYS_PRIVATE) — a "building toward" list is a private intention.
 */
export const DEFAULT_VISIBILITY: ProfileVisibility = {
  identity: 'public',
  sigils: 'public',
  progression: 'private',
}

/** Your goals ("building toward") never leave your own view, whatever the identity flag says. */
export const GOALS_ALWAYS_PRIVATE = true

const SECTIONS: ProfileSection[] = ['identity', 'sigils', 'progression']

function isVisibility(v: unknown): v is Visibility {
  return v === 'public' || v === 'private'
}

/**
 * Read the membrane off an ego node's attributes, tolerant of any junk shape (a missing map, an
 * old write, a hand-edited node): a bad or absent section falls back to its default, never throws.
 */
export function readVisibility(attrs: unknown): ProfileVisibility {
  const map =
    typeof attrs === 'object' && attrs !== null && !Array.isArray(attrs)
      ? (attrs as Record<string, unknown>).profile_visibility
      : undefined
  const src =
    typeof map === 'object' && map !== null && !Array.isArray(map)
      ? (map as Record<string, unknown>)
      : {}
  const out = { ...DEFAULT_VISIBILITY }
  for (const s of SECTIONS) {
    if (isVisibility(src[s])) out[s] = src[s] as Visibility
  }
  return out
}

/** Flip one section's flag, returning the whole (new) membrane — ready to PATCH back wholesale. */
export function toggleSection(current: ProfileVisibility, section: ProfileSection): ProfileVisibility {
  return { ...current, [section]: current[section] === 'public' ? 'private' : 'public' }
}

/** How many sections are public — the "shown in Commune" count for the page's summary line. */
export function publicCount(v: ProfileVisibility): number {
  return SECTIONS.filter((s) => v[s] === 'public').length
}
